import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Section from '../../Section/Section';
import SelectField from '../../ApeUI/SelectField/SelectField';
import ChannelsAiAssistTable from '../../AiAssist/ChannelsAiAssistTable';
import UserProvider from '../../../helpers/UserProvider';
import { snackbarContent, snackbarTopics } from '../../ApeUI/ApeSnackbar/SnackbarContent';
import {
  defaultPick,
  companyOptions,
  modelOptions,
  withCurrentModel,
  labelOfProvider,
} from '../../../utils/aiAssist';
import './GenerateWithAiSection.scss';

// Group-level AI Assist surface. The Section header carries the master enable
// toggle (hard group gate) plus the group-wide Company + Model dropdowns — both
// cascade to every channel on the server (provider/model via a diff-based
// cascade that leaves each channel's individual `enabled` untouched). Below it, a
// per-channel table edits each channel's engines/budget/enabled.
const GenerateWithAiSection = ({ group, onGroupChange }) => {
  const aiAssist = group.aiAssist || {};
  const enabled = Boolean(aiAssist.enabled);
  // Stored pick — undefined when the group never picked a model (then we show the
  // registry default but do NOT store it; an explicit pick is sticky).
  const savedProvider = aiAssist.provider;
  const savedModel = aiAssist.model;

  // Offerable model catalog (source of truth = ai-assist, proxied via users).
  const [models, setModels] = useState([]);
  useEffect(() => {
    let active = true;
    UserProvider.getAiAssistModels().then(list => {
      if (active) {
        setModels(Array.isArray(list) ? list : []);
      }
    });
    return () => { active = false; };
  }, []);

  // Effective selection = explicit pick, else the registry's `default:true` model
  // (resolved from the fetched catalog — the default is owned by ai-assist, not
  // hardcoded here, so it can change without a console change).
  const registryDefault = defaultPick(models);
  const effectiveProvider = savedProvider || registryDefault?.provider;
  const effectiveModel = savedModel || registryDefault?.model;

  // Staged selection — the dropdowns mutate this, NOT the server. Saving on every
  // dropdown click is too dangerous (cascades to every channel); the operator
  // commits with the Apply button. Re-syncs whenever the effective values change
  // (after a successful Apply re-fetch, or once the catalog loads the default).
  const [pending, setPending] = useState({ provider: effectiveProvider, model: effectiveModel });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setPending({ provider: effectiveProvider, model: effectiveModel });
  }, [effectiveProvider, effectiveModel]);

  // Dirty = the operator moved off the effective selection. Picking the same value
  // that's already shown (incl. the default) is not dirty → nothing gets stored.
  const isDirty = pending.provider !== effectiveProvider || pending.model !== effectiveModel;

  const saveGroup = async patch => {
    const response = await UserProvider.updateGroup({ ...group, aiAssist: { ...aiAssist, ...patch } });
    if (response?.success) {
      const message = snackbarContent[snackbarTopics.GROUP_SAVED](group.name)?.message;
      // Re-fetch (don't pass the PUT response): group-wide changes cascade to
      // every channel server-side, so the channel rows must be reloaded.
      onGroupChange(true, message);
    }
    return response;
  };

  // Always keep the current selection visible, even before the catalog loads or
  // if a stored value is no longer offered (offer:false stays runnable).
  const companies = companyOptions(models);
  const companyList = !pending.provider || companies.some(company => company.id === pending.provider)
    ? companies
    : [{ id: pending.provider, name: labelOfProvider(pending.provider) }, ...companies];
  const modelList = withCurrentModel(modelOptions(models, pending.provider), pending.model);

  const onCompanySelect = providerId => {
    if (!providerId || providerId === pending.provider) {
      return;
    }
    // The model must belong to the selected company → move to that provider's
    // first offered model.
    const firstModel = modelOptions(models, providerId)[0];
    setPending({ provider: providerId, model: firstModel ? firstModel.id : pending.model });
  };

  const onModelSelect = modelId => {
    if (!modelId || modelId === pending.model) {
      return;
    }
    setPending(prev => ({ ...prev, model: modelId }));
  };

  const onApply = async () => {
    if (!isDirty || saving) {
      return;
    }
    setSaving(true);
    try {
      await saveGroup({ provider: pending.provider, model: pending.model });
    } finally {
      setSaving(false);
    }
  };

  // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
  const headerExtra = (
    <div className="ai-assist-model-controls" onClick={event => event.stopPropagation()}>
      <SelectField
        name="Company"
        className="ai-assist-select"
        list={companyList}
        activeItem={pending.provider}
        onSelect={onCompanySelect}
        size="small"
        maxFieldWidth="110px"
      />
      <SelectField
        // Remount on provider change: SingleSelectDropdown caches the list into
        // internal state on close (filteredItems), so without a fresh mount it
        // would keep showing the previous provider's models after switching.
        key={pending.provider}
        name="Model"
        className="ai-assist-select"
        list={modelList}
        activeItem={pending.model}
        onSelect={onModelSelect}
        size="small"
        maxFieldWidth="150px"
      />
      {isDirty && (
        <button type="button" className="ai-assist-apply" onClick={onApply} disabled={saving}>
          Apply
        </button>
      )}
    </div>
  );

  const onChannelSave = async (channel, patch) => {
    // provider/model are GROUP-owned (cascaded), not per-channel. Re-assert the
    // group's current pair on every per-channel save so a stale local channel
    // copy can't clobber the group-cascaded model — and a drifted channel
    // self-heals on the next edit. (The per-channel patch only carries
    // enabled / enabledEngines / perChannelMonthlyCap.)
    const groupAi = group.aiAssist || {};
    const updatedAiAssist = {
      ...(channel.aiAssist || {}),
      ...patch,
      provider: groupAi.provider ?? channel.aiAssist?.provider,
      model: groupAi.model ?? channel.aiAssist?.model,
    };
    const response = await UserProvider.updatePublisher({ ...channel, aiAssist: updatedAiAssist }, true);
    if (response?.channel) {
      // Optimistically patch just this channel in the store instead of a full
      // group refetch — a per-channel edit only changes its own row, so
      // refetching the whole group (and remounting the table) on every toggle /
      // engines / budget edit just causes flicker + serial round-trips. (Group-
      // wide changes — master toggle + model Apply — still refetch via saveGroup,
      // since those cascade to every channel server-side.)
      const key = channel.publisherId || channel._id;
      const channels = (group.channels || []).map(item => (
        (item.publisherId || item._id) === key ? { ...item, aiAssist: updatedAiAssist } : item
      ));
      onGroupChange(true, undefined, { ...group, channels });
    }
    return response;
  };

  // Enabling AI must STORE an explicit pick — ai-assist rejects an enabled channel
  // with no {provider, model} (no silent runtime default). Store the pre-selected
  // registry default alongside `enabled` so the group + cascaded channels always
  // carry a real pick. Guarded so we never store a null pick before the catalog
  // resolves the default (effective is immediate for a group with a saved pick;
  // for an un-picked group it's ready as soon as the /ai-assist/models fetch lands).
  const onToggleEnabled = value => {
    if (!value) {
      saveGroup({ enabled: false });
      return;
    }
    if (!effectiveProvider || !effectiveModel) {
      return;
    }
    saveGroup({ enabled: true, provider: effectiveProvider, model: effectiveModel });
  };

  return (
    <Section
      title="GENERATE WITH AI"
      includeToggleButton
      isChecked={enabled}
      onToggle={onToggleEnabled}
      headerExtra={headerExtra}
    >
      <ChannelsAiAssistTable
        channels={group.channels}
        groupEnabled={enabled}
        onChannelSave={onChannelSave}
      />
    </Section>
  );
};

GenerateWithAiSection.propTypes = {
  group: PropTypes.shape({
    name: PropTypes.string,
    aiAssist: PropTypes.shape({}),
    channels: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onGroupChange: PropTypes.func.isRequired,
};

export default GenerateWithAiSection;
